// =============================================================================
// La règle de parcours par état (DEV-2284, lot C ; cible v0.3 §11.1, maquette
// v0.4 « La maquette de la page », `ETATS` et `forcesParEtat`).
//
// Ce que le courtier doit vivre : un refus s'ouvre sur son remède ; l'ordre
// des sections est le même dans tous les cas ; aucun repli ne cache un fait.
//
// Une seule fonction PURE rend, pour l'étude affichée, section par section :
// le statut (ouverte d'office, repliée, sans objet, absente) et sa raison, la
// section d'ouverture et l'action principale. La page en tire ce que l'état
// ouvre d'office ; la priorité reste état, puis geste (`?ouvrir=`), puis
// mémoire de la personne, puis premier jour (`optim-turpe-v2.ouvertures.ts`).
//
// La liste fermée des vingt-deux faits jamais masqués (maquette, partie « Les
// en-têtes », « Ce qui n'est jamais masqué ») est ici aussi : chaque fait dit
// où il se lit sans clic, et `ouSeLitLeFait` le cherche sur la page la plus
// repliée qui puisse exister dans l'état (tout replié à la main, seul l'état
// ouvre). C'est le test d'acceptation des niveaux, rejoué dans chaque état.
// =============================================================================
import {
    enteteAgir,
    enteteChoisir,
    entetePartager,
    etatDuParcours,
    type ContexteEntetes,
    type Entete,
} from "@modules/courtage/utils/optim-turpe-v2.entetes"
import {
    lireEchec,
    lireLeviersDuRefus,
    CAUSES_AU_REMEDE_HORS_PAGE,
    REMEDE_PAR_CAUSE,
} from "@modules/courtage/utils/optim-turpe-v2.echec"
import { ancreDepuisEchec } from "@modules/courtage/utils/optim-turpe-v2.completer"
import {
    estC5,
    getMeilleurePeriode,
} from "@modules/courtage/utils/optim-turpe-v2.helpers"
import type { OptimTurpeV2Etude } from "@modules/courtage/types/optim-turpe-v2"

/** Les états de la règle (une ligne de la table 11.1 chacun). */
export type EtatDeLaRegle =
    | "economie"
    | "deja_optimale"
    | "surcout"
    | "montant_non_servi"
    | "refus_avec_levier"
    | "refus_sans_levier"
    | "c5"
    | "scenario_saisi"
    | "vide"
    | "inconnu"

/** Les sections que la règle gouverne, dans l'ordre de la page. */
export const SECTIONS_DE_LA_REGLE = ["cadre", "t1", "t2", "t3", "t4"] as const
export type SectionDeLaRegle = (typeof SECTIONS_DE_LA_REGLE)[number]

export type StatutDeSection =
    | "ouverte_d_office"
    | "repliee"
    | "sans_objet"
    | "absente"

export type StatutAvecRaison = { statut: StatutDeSection; raison: string }

export type CodeAction =
    | "comparer_les_scenarios"
    | "retenir_votre_structure"
    | "telecharger_le_rapport"
    | "relancer_sur_la_periode"
    | "modifier_le_cadre"
    | "levier_servi"
    | "demander_de_l_aide"
    | "lancer_l_etude"
    | "aucune"

export type ActionPrincipale = { code: CodeAction; section: SectionDeLaRegle }

export type RegleDeParcours = {
    etat: EtatDeLaRegle
    /** La section sur laquelle la page s'ouvre. */
    ouvreSur: "cadre" | "t1"
    sections: Record<SectionDeLaRegle, StatutAvecRaison>
    action: ActionPrincipale
}

// -----------------------------------------------------------------------------
// Le refus : a-t-il un levier ?
// -----------------------------------------------------------------------------

/**
 * Un refus a un levier quand le moteur en sert un (un champ à corriger, une
 * période à étudier), ou quand la page en connaît un pour ce code : un remède
 * écrit (`REMEDE_PAR_CAUSE`) ou un champ à compléter (`ANCRE_PAR_CAUSE`, le
 * geste « Compléter »). `levier: "aucun"` est une réponse pleine : sans
 * levier. Le levier du relais « Faire relire cette étude », que le moteur sert
 * encore, n'en est pas un : ce bloc est retiré de la page (DEV-2311) ; un refus
 * sans levier renvoie à l'aide de l'application, au temps 1.
 *
 * Relu en base (lecture seule, 01/10/2026) : la plupart des refus de 2026 ne
 * servent ni `levier` ni `leviers` (donnee-trous-present, donnee-plage-sans-
 * puissance, donnee-aucune-disponible, validation-fta-manquante…), alors que
 * changer la période ou compléter le champ est leur remède.
 */
export function refusALevier(etude: OptimTurpeV2Etude): boolean {
    const echec = lireEchec(etude)
    if (!echec) return false
    if (echec.levier === "aucun") return false
    if (echec.levier) return true
    const leviers = lireLeviersDuRefus(etude)
    if (leviers.length > 0) return true
    if (echec.meilleurePeriode || getMeilleurePeriode(etude)) return true
    if (ancreDepuisEchec(echec) !== null) return true
    return (
        echec.code !== null &&
        echec.code in REMEDE_PAR_CAUSE &&
        !CAUSES_AU_REMEDE_HORS_PAGE.has(echec.code)
    )
}

// -----------------------------------------------------------------------------
// La règle
// -----------------------------------------------------------------------------

function deLaForme(e: Entete, raison: string): StatutAvecRaison {
    switch (e.forme) {
        case "absente":
            return { statut: "absente", raison: "aucun sens dans cet état" }
        case "sans_objet":
            return { statut: "sans_objet", raison: e.noyau ?? raison }
        default:
            return { statut: "repliee", raison }
    }
}

const OUVERTE = (raison: string): StatutAvecRaison => ({
    statut: "ouverte_d_office",
    raison,
})
const REPLIEE = (raison: string): StatutAvecRaison => ({
    statut: "repliee",
    raison,
})

export function etatDeLaRegle(ctx: ContexteEntetes): EtatDeLaRegle {
    const etat = etatDuParcours(ctx)
    if (etat === "refus") {
        const refuse = ctx.refus ?? ctx.etude
        return refuse && refusALevier(refuse)
            ? "refus_avec_levier"
            : "refus_sans_levier"
    }
    if (etat === "economie" && ctx.etude && estC5(ctx.etude)) return "c5"
    return etat
}

export function regleDeParcours(ctx: ContexteEntetes): RegleDeParcours {
    const etat = etatDeLaRegle(ctx)
    const etude = ctx.refus ?? ctx.etude
    const t2 = deLaForme(enteteChoisir(ctx), "en-tête porteur")
    const t3 = deLaForme(entetePartager(ctx), "en-tête porteur")
    const t4 = deLaForme(enteteAgir(ctx), "en-tête porteur")
    const sections: Record<SectionDeLaRegle, StatutAvecRaison> = {
        cadre: REPLIEE("la ligne du cadre dit sur quoi porte le chiffre"),
        t1: OUVERTE("le temps 1 ne se replie jamais"),
        t2,
        t3,
        t4,
    }
    let ouvreSur: RegleDeParcours["ouvreSur"] = "t1"
    let action: ActionPrincipale = {
        code: "telecharger_le_rapport",
        section: "t3",
    }

    // Les deux scénarios diffèrent sur la part ferme et aucun n'est retenu au
    // journal (il n'est pas servi) : le choix n'est pas un détail (fait 20).
    const choixOuvert =
        ctx.arbitragePertinent === true && t2.statut === "repliee"

    switch (etat) {
        case "vide":
            sections.cadre = OUVERTE("aucune étude : le cadre la lance")
            sections.t1 = {
                statut: "sans_objet",
                raison: "aucune étude pour ce compteur",
            }
            ouvreSur = "cadre"
            action = { code: "lancer_l_etude", section: "cadre" }
            break
        case "refus_avec_levier":
            sections.cadre = OUVERTE("le remède se pose dans le cadre")
            ouvreSur = "cadre"
            action = { code: "levier_servi", section: "cadre" }
            break
        case "refus_sans_levier":
            // Aucun levier : la cause et l'aide de l'application se lisent au
            // temps 1, qui ne se replie jamais (DEV-2311).
            action = { code: "demander_de_l_aide", section: "t1" }
            break
        case "montant_non_servi":
            // Maquette « mns » : le panneau du cadre est ouvert, que le moteur
            // serve ou non une période plus complète. Quand il la sert, l'action
            // principale est « Relancer sur cette période », dans la conclusion.
            sections.cadre = OUVERTE(
                "aucun montant : la page s'ouvre sur ce qui le rendrait servi"
            )
            action =
                etude && getMeilleurePeriode(etude)
                    ? { code: "relancer_sur_la_periode", section: "t1" }
                    : { code: "modifier_le_cadre", section: "cadre" }
            break
        case "surcout":
            if (choixOuvert) {
                sections.t2 = OUVERTE("les deux scénarios diffèrent")
                action = { code: "comparer_les_scenarios", section: "t2" }
            } else {
                action = { code: "modifier_le_cadre", section: "cadre" }
            }
            break
        case "scenario_saisi":
            action = { code: "retenir_votre_structure", section: "t2" }
            break
        case "economie":
            if (choixOuvert) {
                sections.t2 = OUVERTE("les deux scénarios diffèrent")
                action = { code: "comparer_les_scenarios", section: "t2" }
            }
            break
        case "inconnu":
            action = { code: "aucune", section: "t1" }
            break
        default:
            // déjà optimale, C5 : vers le rapport.
            break
    }
    return { etat, ouvreSur, sections, action }
}

/** Ce que l'état ouvre d'office, pour la mécanique des ouvertures. */
export function forcesDeLaRegle(regle: RegleDeParcours): {
    cadre: boolean
    t2: boolean
    t3: boolean
    t4: boolean
} {
    const o = (s: SectionDeLaRegle) =>
        regle.sections[s].statut === "ouverte_d_office"
    return {
        cadre: o("cadre"),
        t2: o("t2"),
        t3: o("t3"),
        t4: o("t4"),
    }
}

// -----------------------------------------------------------------------------
// Les vingt-deux faits jamais masqués (liste fermée de la maquette v0.4)
// -----------------------------------------------------------------------------

/**
 * Où un fait se lit. Le bandeau, le temps 1 et la ligne du cadre ne se
 * replient jamais ; un en-tête se lit replié ; `cadre` désigne le CORPS de
 * cette section, lisible seulement quand l'état l'ouvre.
 */
export type LieuDuFait =
    | "bandeau"
    | "t1"
    | "ligne_du_cadre"
    | "cadre"
    | "entete_t2"
    | "entete_t3"
    | "entete_t4"
    | "corps_t2"

export type FaitJamaisMasque = {
    numero: number
    fait: string
    /** Les lieux où il se lit sans clic, d'après la maquette. */
    lieux: LieuDuFait[]
    /** Les faits 10, 14 et 16 se lisent en partie au premier geste. */
    premierGeste?: boolean
    /**
     * Où il se lit au premier geste, quand aucun lieu n'est lisible sans clic :
     * `corps_t3` = « À savoir avant d'envoyer » et « Limites de l'étude », au
     * premier niveau de « Rapport et demande » déplié (Q36 du 14/10/2026 : la
     * demande y est regroupée), à côté de « Copier la demande au fournisseur »
     * (preuve DOM : OptimTurpeV2Demande.spec).
     */
    auPremierGeste?: "corps_t3"[]
}

export const FAITS_JAMAIS_MASQUES: readonly FaitJamaisMasque[] = [
    {
        numero: 1,
        fait: "le montant net et ses deux parts",
        lieux: ["t1", "bandeau"],
    },
    {
        numero: 2,
        fait: "la période et sa durée",
        lieux: ["t1", "bandeau", "ligne_du_cadre"],
    },
    { numero: 3, fait: "l'état servi par le moteur", lieux: ["t1", "bandeau"] },
    {
        numero: 4,
        fait: "le montant non servi et sa cause",
        lieux: ["t1", "bandeau"],
    },
    {
        numero: 5,
        fait: "chaque réserve, sur la valeur qu'elle touche",
        lieux: ["t1", "ligne_du_cadre", "entete_t2", "entete_t3"],
    },
    { numero: 6, fait: "la confiance", lieux: ["t1"] },
    { numero: 7, fait: "le dernier jour mesuré", lieux: ["ligne_du_cadre"] },
    {
        numero: 8,
        fait: "le refus, sa cause vraie et son levier",
        lieux: ["t1", "cadre"],
    },
    {
        numero: 9,
        fait: "la provenance des entrées",
        lieux: ["ligne_du_cadre", "t1"],
    },
    {
        numero: 10,
        fait: "la limite de raccordement",
        lieux: ["ligne_du_cadre", "entete_t2"],
        premierGeste: true,
    },
    {
        numero: 11,
        fait: "le scénario affiché, nommé partout où il change un chiffre",
        lieux: ["bandeau", "entete_t2", "entete_t3"],
    },
    {
        numero: 12,
        fait: "« Vos puissances : ce n'est pas la recommandation »",
        lieux: ["t1", "entete_t3"],
    },
    {
        numero: 13,
        fait: "un surcoût ne se lit jamais comme une économie",
        lieux: ["t1", "bandeau"],
    },
    {
        numero: 14,
        fait: "la date du calcul, du barème, la référence",
        lieux: ["bandeau", "entete_t3"],
        premierGeste: true,
    },
    {
        numero: 15,
        fait: "la marque : jamais le nom de la maison",
        lieux: ["bandeau", "t1", "ligne_du_cadre", "entete_t2", "entete_t3"],
    },
    {
        numero: 16,
        fait: "ce que l'étude ne prend pas en compte",
        lieux: [],
        premierGeste: true,
        auPremierGeste: ["corps_t3"],
    },
    {
        numero: 17,
        fait: "une modification contractuelle récente",
        lieux: ["entete_t3", "t1"],
    },
    {
        numero: 18,
        fait: "déclaré ou constaté, et par qui",
        lieux: ["entete_t3", "bandeau"],
    },
    {
        numero: 19,
        fait: "le compteur, le segment, l'unité",
        lieux: ["bandeau"],
    },
    {
        numero: 20,
        fait: "les deux scénarios et leurs montants, quand ils diffèrent",
        lieux: ["corps_t2", "entete_t2", "bandeau"],
    },
    {
        numero: 21,
        fait: "un réglage avancé posé sur l'étude",
        lieux: ["ligne_du_cadre"],
    },
    {
        numero: 22,
        fait: "une étape déjà établie de la démarche",
        lieux: ["entete_t3", "bandeau"],
    },
]

/**
 * Les lieux lisibles sans clic sur la page la plus repliée de cet état : tout
 * replié à la main, seul l'état ouvre. Une section absente n'a pas d'en-tête ;
 * une section sans objet garde le sien, en une ligne.
 */
export function lieuxSansClic(regle: RegleDeParcours): Set<LieuDuFait> {
    const s = regle.sections
    const present = (x: SectionDeLaRegle) => s[x].statut !== "absente"
    const ouvert = (x: SectionDeLaRegle) => s[x].statut === "ouverte_d_office"
    const lieux = new Set<LieuDuFait>(["bandeau"])
    if (present("t1")) lieux.add("t1")
    if (present("cadre")) lieux.add("ligne_du_cadre")
    if (ouvert("cadre")) lieux.add("cadre")
    if (present("t2")) lieux.add("entete_t2")
    if (present("t3")) lieux.add("entete_t3")
    if (present("t4")) lieux.add("entete_t4")
    if (ouvert("t2")) lieux.add("corps_t2")
    return lieux
}

/** Où le fait se lit sans clic dans cet état, ou `[]` (il serait masqué). */
export function ouSeLitLeFait(
    regle: RegleDeParcours,
    fait: FaitJamaisMasque
): LieuDuFait[] {
    const lieux = lieuxSansClic(regle)
    return fait.lieux.filter((l) => lieux.has(l))
}
